import * as React from "react";
import { useEffect, useState } from "react";
import { useGetCockpitDataMutation } from "src/context/api/cockpit/api";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { Autocomplete, Dialog, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Select, TextField, useTheme } from '@mui/material';
import Typography from "@mui/material/Typography";
import {
    DataGridPro,
    GridColDef,
    GridRenderCellParams,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarExport,
    GridToolbarFilterButton,
    GridPagination,
    GridColumnVisibilityModel,
} from "@mui/x-data-grid-pro";
import Skeleton from "@mui/material/Skeleton";
import { formatCurrency } from "src/utils/format-currency";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import CompareChip from "src/components/chip/compare-chip";
import CustomLineChart from "src/components/inline-chart/inline-chart";
import { formatNumber, fPercent } from "src/utils/format-number";
import Iconify from "src/components/iconify";
import Tooltip from "@mui/material/Tooltip";
import { useRouter } from "src/routes/hooks/use-router";
import WorkspaceCreate from "src/sections/workspaces/workspace-create";
import { t } from "i18next";
import { useBoolean } from "src/hooks/use-boolean";
import { useCurrentUserQuery } from "src/context/api/auth";
import { useGetWorkspacesQuery } from "src/context/api/workspaces";
import { setFilterCompare, setFilterDate, setFilterDateCompare } from "src/context/reducers/filter-settings/filterSlice";
import { useDispatch } from "react-redux";
import { subDays } from "date-fns";

export default function Cockpit() {
    const [getCockpitData, { data, isLoading, error }] = useGetCockpitDataMutation();
    const [treeRows, setTreeRows] = useState<any[]>([]);
    const workspace = useCurrentWorkspace();
    const lang = localStorage.getItem("i18nextLng") || "en";
    const theme = useTheme();
    const router = useRouter();
    const user: any = useCurrentUserQuery();
    const workspaces = useGetWorkspacesQuery(null);
    const role = user?.data?.role;
    const newWorkspaceModal = useBoolean(false);
    const dispatch = useDispatch();

    useEffect(() => {
        if (["admin", "brand specialist"].includes(role) && location?.pathname?.split("/")[2] === "add") {
            newWorkspaceModal.onTrue()
        }
    }, [location.pathname])

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 10,
        page: 0,
    });

    const dateRangeOptions = [
        { label: 'Last 7 days', value: 'last_7' },
        { label: 'Last 14 days', value: 'last_14' },
        { label: 'Last 30 days', value: 'last_30' },
    ];

    const comparisonPeriodOptions = [
        { label: 'Prev. Period', value: 'prev_period' },
        { label: 'Last Year', value: 'last_year' },
    ];

    const tabs = [
        {
            label: 'All',
            countBadge: theme.palette.mode === 'dark' ? "white" : "#1c252e",
            color: theme.palette.mode === 'dark' ? "#1c252e" : "white",
            countBadgeActive: theme.palette.mode === 'dark' ? "white" : "#1c252e",
            colorActive: theme.palette.mode === 'dark' ? "#1c252e" : "white"
        },
        {
            label: 'Blended CTR dropped of 3pp',
            countBadge: theme.palette.mode === 'dark' ? "#402d2f" : "#ffe4de",
            color: theme.palette.mode === 'dark' ? "#ffac82" : "#b71d18",
            countBadgeActive: "#ff5630",
            colorActive: "white"
        },
        {
            label: 'Blended CPC increased of 5%',
            countBadge: theme.palette.mode === 'dark' ? "#403a27" : "#fff1d6",
            color: theme.palette.mode === 'dark' ? "#919eab" : "#b76e00",
            countBadgeActive: "#ffab00",
            colorActive: "#1c252e"
        },
        {
            label: 'Brand Searches increased of 10%',
            countBadge: theme.palette.mode === 'dark' ? "#2e3842" : "#edeff1",
            color: theme.palette.mode === 'dark' ? "#919eab" : "#637381",
            countBadgeActive: theme.palette.mode === 'dark' ? "#ffffff" : "#1c252e",
            colorActive: theme.palette.mode === 'dark' ? "#1c252e" : "#ffffff",
        }
    ];


    const [selectedTab, setSelectedTab] = useState('All');
    const [selectedDateRange, setSelectedDateRange] = useState(dateRangeOptions[0]);
    const [selectedComparisonPeriod, setSelectedComparisonPeriod]: any = useState(comparisonPeriodOptions[0]);

    const TooltipInfoValue = ({ labelText, valueText }: any) => {
        const textColor = theme.palette.mode === 'light' ? '#212B36' : '#fff';
        const tooltipBgColor = theme.palette.mode === 'light' ? '#fff' : 'rgb(31, 38, 49)';
        const innerBgColor = theme.palette.mode === 'light' ? '#F4F6F8' : '#191f28';

        return (
            <div
                style={{
                    zIndex: 9900,
                    borderRadius: 8,
                    color: textColor,
                    lineHeight: 3.2,
                    textAlign: "center",
                    backgroundColor: tooltipBgColor,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                }}
            >
                <div style={{
                    textAlign: "center",
                    lineHeight: 2,
                    marginBottom: 1,
                }}>
                    <span style={{
                        fontWeight: 600,
                        fontSize: 12,
                        fontFamily: "Public Sans"
                    }}>{selectedComparisonPeriod?.label}</span>
                </div>
                <div style={{
                    backgroundColor: innerBgColor,
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 1,
                    padding: "2px 8px",
                    borderRadius: 8
                }}>
                    <span style={{ fontSize: 12 }}>{labelText}</span>
                    <span style={{ fontSize: 12, fontWeight: "bold", marginLeft: 4 }}>{valueText}</span>
                </div>
            </div>
        );
    }

    // Création des lignes en treeData
    useEffect(() => {
        if (!data) return;
        let rows: any[] = [];

        data.forEach((e: any) => {
            // --- Transformer le tableau 'regions' en maps et sparklines par pays ---
            const adCostByCountry: Record<string, number> = {};
            const adCostByCountryCompare: Record<string, number> = {};
            const blendedCtrByCountry: Record<string, number> = {};
            const blendedCtrByCountryCompare: Record<string, number> = {};
            const sparklineByCountry: Record<string, { date: string; value: number }[]> = {};
            const sparklineBlendedCtrByCountry: Record<string, { date: string; value: number }[]> = {};
            const sparklineBlendedCpcByCountry: Record<string, { date: string; value: number }[]> = {};
            const activationsByCountry: Record<string, string[]> = {};
            const brandSearchesByCountry: Record<string, number> = {};
            const brandSearchesByCountryCompare: Record<string, number> = {};
            const sparklineBrandSearchesByCountry: Record<string, { date: string; value: number }[]> = {};
            const sparklineBrandConversionsByCountry: Record<string, { date: string; value: number }[]> = {};
            const sparklineAdCostActivatedByCountry: Record<string, { date: string; value: number }[]> = {};
            const adCostActivatedByCountry: Record<string, number> = {};
            const adCostActivatedByCountryCompare: Record<string, number> = {};
            const blendedCtrActivatedByCountry: Record<string, number> = {};
            const blendedCtrActivatedByCountryCompare: Record<string, number> = {};
            const sparklineBlendedCtrActivatedByCountry: Record<string, { date: string, value: number }[]> = {};
            const blendedCpcActivatedByCountry: Record<string, number> = {};
            const blendedCpcActivatedByCountryCompare: Record<string, number> = {};
            const sparklineBlendedCpcActivatedByCountry: Record<string, { date: string; value: number }[]> = {};
            const blendedCpcByCountry: Record<string, number> = {};
            const blendedCpcByCountryCompare: Record<string, number> = {};

            (e.regions || []).forEach((r: any) => {
                const c = r.region_name;
                adCostByCountry[c] = r.ad_cost_by_region;
                adCostByCountryCompare[c] = r.ad_cost_by_region_compare || 0;
                blendedCpcByCountry[c] = r.blended_cpc_by_region || 0;
                blendedCpcByCountryCompare[c] = r.blended_cpc_by_region_compare || 0;
                blendedCtrByCountry[c] = r.blended_ctr_by_region;
                blendedCtrByCountryCompare[c] = r.blended_ctr_by_region_compare || 0; 0
                sparklineByCountry[c] = (r.sparkline_ad_cost_by_region || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_ad_cost_by_region }));
                sparklineBlendedCtrByCountry[c] = (r.sparkline_ctr_by_region || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_ctr_by_region }));
                sparklineBlendedCpcByCountry[c] = (r.sparkline_cpc_by_region || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_cpc_by_region }));
                activationsByCountry[c] = r.activations_by_region || [];
                brandSearchesByCountry[c] = r.brand_searches_by_region;
                brandSearchesByCountryCompare[c] = r.brand_searches_by_region_compare || 0;
                sparklineBrandSearchesByCountry[c] = (r.sparkline_brand_searches_by_region || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_brand_searches_by_region }));
                sparklineBrandConversionsByCountry[c] = (r.sparkline_conversions_by_region || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_conversions_by_region }));
                sparklineAdCostActivatedByCountry[c] = (r.sparkline_ad_cost_activated_by_region || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_ad_cost_activated_by_region }));
                adCostActivatedByCountry[c] = r.ad_cost_activated_by_region || 0;
                adCostActivatedByCountryCompare[c] = r.ad_cost_activated_by_region_compare || 0;
                blendedCtrActivatedByCountry[c] = r.blended_ctr_activated_by_region || 0;
                blendedCtrActivatedByCountryCompare[c] = r.blended_ctr_activated_by_region_compare || 0;
                sparklineBlendedCtrActivatedByCountry[c] = (r.sparkline_blended_ctr_activated_by_region || []).map((pt: any) => ({ date: pt.date_day, value: pt.blended_ctr_activated }));
                blendedCpcActivatedByCountry[c] = r.blended_cpc_activated_by_region || 0;
                blendedCpcActivatedByCountryCompare[c] = r.blended_cpc_activated_by_region_compare || 0;
                sparklineBlendedCpcActivatedByCountry[c] = (r.sparkline_blended_cpc_activated_by_region || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_blended_cpc_activated_by_region }));
            });


            // Union global des activations
            const globalActivations = Array.from(new Set(Object.values(activationsByCountry).flat()));

            // Remapper les sparklines globales
            const sparkline = (e.sparkline_ad_cost || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_ad_cost }));
            const sparklineBlendedCtr = (e.sparkline_ctr || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_ctr }));
            const sparklineBlendedCpc = (e.sparkline_cpc || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_cpc }));
            const sparklineBrandSearches = (e.sparkline_brand_searches || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_brand_searches }));
            const sparklineBrandConversions = (e.sparkline_conversions || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_conversions }));
            const sparklineAdCostActivated = (e.sparkline_ad_cost_activated || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_ad_cost_activated }));
            const sparklineCtrActivated = (e.sparkline_ctr_activated || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_ctr_activated }));
            const sparklineCpcActivated = (e.sparkline_cpc_activated || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_cpc_activated }));

            // Pusher les lignes parent/enfant selon nombre de pays
            const countries = Object.entries(adCostByCountry);
            if (countries.length === 1) {
                const [country, cost] = countries[0];
                rows.push({
                    id: e.client,
                    client: e.client,
                    isParent: true,
                    ad_cost: e.ad_cost_total,
                    ad_cost_compare: e.ad_cost_total_compare,
                    blended_ctr: e.blended_ctr_total,
                    blended_ctr_compare: e.blended_ctr_total_compare,
                    sparkline_blended_ctr: sparklineBlendedCtr,
                    sparkline: sparkline,
                    activations: globalActivations,
                    brand_searches_total: e.brand_searches_total,
                    brand_searches_total_compare: e.brand_searches_total_compare,
                    sparkline_brand_searches: sparklineBrandSearches,
                    blended_cpc_total: e.blended_cpc_total,
                    blended_cpc_total_compare: e.blended_cpc_total_compare,
                    sparkline_blended_cpc: sparklineBlendedCpc,
                    blended_conversions_total: e.blended_conversions_total,
                    blended_conversions_total_compare: e.blended_conversions_total_compare,
                    sparkline_brand_conversions: sparklineBrandConversions,
                    workspace_name: e.workspace_name,
                    workspace_template: e.workspace_template,
                    workspace_id: e.workspace_id,
                    workspace_currency: e.workspace_currency,
                    ad_cost_activated: e.ad_cost_activated,
                    ad_cost_activated_compare: e.ad_cost_activated_compare,
                    blended_ctr_activated: e.blended_ctr_activated,
                    blended_ctr_activated_compare: e.blended_ctr_activated_compare,
                    blended_cpc_activated: e.blended_cpc_activated,
                    blended_cpc_activated_compare: e.blended_cpc_activated_compare,
                    sparkline_ad_cost_activated: sparklineAdCostActivated,
                    sparkline_ctr_activated: sparklineCtrActivated,
                    sparkline_cpc_activated: sparklineCpcActivated
                });
            } else {
                // ligne parent
                rows.push({
                    id: e.client,
                    client: e.client,
                    isParent: true,
                    ad_cost: e.ad_cost_total,
                    ad_cost_compare: e.ad_cost_total_compare,
                    blended_ctr: e.blended_ctr_total,
                    blended_ctr_compare: e.blended_ctr_total_compare,
                    sparkline_blended_ctr: sparklineBlendedCtr,
                    sparkline: sparkline,
                    activations: globalActivations,
                    brand_searches_total: e.brand_searches_total,
                    brand_searches_total_compare: e.brand_searches_total_compare,
                    sparkline_brand_searches: sparklineBrandSearches,
                    blended_cpc_total: e.blended_cpc_total,
                    blended_cpc_total_compare: e.blended_cpc_total_compare,
                    sparkline_blended_cpc: sparklineBlendedCpc,
                    blended_conversions_total: e.blended_conversions_total,
                    blended_conversions_total_compare: e.blended_conversions_total_compare,
                    sparkline_brand_conversions: sparklineBrandConversions,
                    workspace_name: e.workspace_name,
                    workspace_template: e.workspace_template,
                    workspace_id: e.workspace_id,
                    workspace_currency: e.workspace_currency,
                    ad_cost_activated: e.ad_cost_activated,
                    ad_cost_activated_compare: e.ad_cost_activated_compare,
                    blended_ctr_activated: e.blended_ctr_activated,
                    blended_ctr_activated_compare: e.blended_ctr_activated_compare,
                    blended_cpc_activated: e.blended_cpc_activated,
                    blended_cpc_activated_compare: e.blended_cpc_activated_compare,
                    sparkline_ad_cost_activated: sparklineAdCostActivated,
                    sparkline_ctr_activated: sparklineCtrActivated,
                    sparkline_cpc_activated: sparklineCpcActivated
                });

                // lignes enfants par pays
                countries.forEach(([c, cost]) => {
                    rows.push({
                        id: `${e.client}__${c}`,
                        client: e.client,
                        country: c,
                        parentId: e.client,
                        isParent: false,
                        ad_cost: cost,
                        ad_cost_compare: adCostByCountryCompare[c] || 0,
                        blended_ctr: blendedCtrByCountry[c] || 0,
                        blended_ctr_compare: blendedCtrByCountryCompare[c] || 0,
                        sparkline: sparklineByCountry[c] || [],
                        sparkline_blended_ctr: sparklineBlendedCtrByCountry[c] || [],
                        sparkline_blended_cpc: sparklineBlendedCpcByCountry[c] || [],
                        activations: activationsByCountry[c] || [],
                        brand_searches_total: brandSearchesByCountry[c] || 0,
                        brand_searches_total_compare: brandSearchesByCountryCompare[c] || 0,
                        sparkline_brand_searches: sparklineBrandSearchesByCountry[c] || [],

                        blended_cpc_total: blendedCpcByCountry[c] || 0,
                        blended_cpc_total_compare: blendedCpcByCountryCompare[c] || 0,

                        sparkline_brand_conversions: sparklineBrandConversionsByCountry[c] || [],
                        workspace_name: e.workspace_name,
                        workspace_template: e.workspace_template,
                        workspace_currency: e.workspace_currency,
                        ad_cost_activated: adCostActivatedByCountry[c] || 0,
                        ad_cost_activated_compare: adCostActivatedByCountryCompare[c] || 0,
                        sparkline_ad_cost_activated: sparklineAdCostActivatedByCountry[c] || [],
                        blended_ctr_activated: blendedCtrActivatedByCountry[c] || 0,
                        blended_ctr_activated_compare: blendedCtrActivatedByCountryCompare[c] || 0,
                        sparkline_ctr_activated: sparklineBlendedCtrActivatedByCountry[c] || [],
                        blended_cpc_activated: blendedCpcActivatedByCountry[c] || 0,
                        blended_cpc_activated_compare: blendedCpcActivatedByCountryCompare[c] || 0,
                        sparkline_cpc_activated: sparklineBlendedCpcActivatedByCountry[c] || [],
                    });
                });
            }
        });

        setTreeRows(rows);
    }, [data]);

    // ACTIONS
    const ActionMenu = ({ params }: { params: any }) => {
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
        const open = Boolean(anchorEl);

        const handleClick = (event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            setAnchorEl(event.currentTarget);
        };

        const handleClose = (event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            setAnchorEl(null);
        };

        const handleRedirectTrafficDetails = () => {
            router.push(`/optimization/traffic-details?workspace=${params?.row?.workspace_id}`);
        }


        const handleRedirectActivate = () => {
            router.push(`/optimization/activations/ko-bidding/max-cpc?workspace=${params?.row?.workspace_id}`);
        }

        return (
            <>
                <IconButton onClick={handleClick} sx={{ cursor: 'pointer' }}>
                    <Iconify width={13} height={13} icon="fa-solid:ellipsis-v" />
                </IconButton>
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    onClick={(e) => e.stopPropagation()}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <MenuItem onClick={handleRedirectTrafficDetails}>
                        <Iconify icon={"fluent:arrow-trending-lines-20-filled"} />

                        <Box sx={{ position: 'relative', top: 2 }}>
                            <Typography sx={{ fontSize: 14, ml: .5 }}>Analyse</Typography>
                        </Box>
                    </MenuItem>
                    <MenuItem onClick={handleRedirectActivate}>
                        <Iconify icon={"mdi:gear"} />

                        <Box sx={{ position: 'relative', top: 2 }}>
                            <Typography sx={{ fontSize: 14, ml: .5 }}>Activate</Typography>
                        </Box>
                    </MenuItem>
                </Menu>
            </>
        );
    }

    // TOUTES TES COLONNES, sans rien perdre
    const columns: GridColDef[] = [
        {
            field: 'workspace_name',
            headerName: 'Workspace',
            width: 250,
            disableColumnMenu: true,
            filterable: true,
            sortable: true,
        },
        {
            field: "ad_cost",
            headerName: "Ad Cost",
            width: 280,
            type: "number",
            renderCell: (params: GridRenderCellParams<any>) => {
                const cur = params.row.ad_cost as number;
                const prev = params.row.ad_cost_compare as number;
                const pct = prev ? ((cur - prev) / prev) * 100 : NaN;

                const sparkSeries = [
                    {
                        name: 'Ad Cost',
                        data: ((params.row.sparkline || []) as { date: string; value: number }[]).map(
                            (pt) => ({ x: new Date(pt.date).getTime(), y: pt.value })
                        ),
                    },
                ];

                return (
                    <Stack direction="row" alignItems="center" sx={{ flexDirection: 'row', justifyContent: 'space-between', overflow: 'visible' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', fontSize: 12 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    position: 'relative',
                                    top: -6,
                                    mr: 1,
                                    overflow: 'visible'
                                }}>
                                <CustomLineChart
                                    series={sparkSeries}
                                    type="bar"
                                    color="#FFD066"
                                    height={30}
                                    width={100}
                                    formatValue={(value: any) =>
                                        formatCurrency(value, params.row.workspace_currency || workspace?.currency, lang, true, 2)
                                    }
                                />
                            </Box>
                            {formatCurrency(cur, params.row.workspace_currency || workspace?.currency, lang, true, 2)}
                        </Box>

                        <Tooltip
                            title={
                                <TooltipInfoValue
                                    labelText="Ad Cost:"
                                    valueText={formatCurrency(prev, params.row.workspace_currency || workspace?.currency, lang, true, 2)} z
                                />
                            }
                            placement="top"
                            arrow
                            slotProps={{
                                popper: {
                                    sx: {
                                        [`& .MuiTooltip-tooltip`]: {
                                            backgroundColor: 'transparent',
                                        },
                                        [`& .MuiTooltip-arrow`]: {
                                            color: 'rgb(31, 38, 49)', // même couleur pour l'arrow !
                                        },
                                    }
                                }
                            }}
                            PopperProps={{ disablePortal: true }}
                        >
                            <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
                                <CompareChip invertedColor={true} value={pct} />
                            </Box>
                        </Tooltip>
                    </Stack>
                );
            }
        },
        {
            field: "blended_ctr",
            headerName: "Blended CTR",
            width: 265,
            type: "number",
            renderCell: (params: GridRenderCellParams<any>) => {
                const cur = params.row.blended_ctr * 100 as number;
                const prev = params.row.blended_ctr_compare * 100 as number;
                const delta_pp = cur - prev;

                const sparkSeries = [
                    {
                        name: 'Blended CTR',
                        data: ((params.row.sparkline_blended_ctr || []) as { date: string; value: number }[]).map(
                            (pt) => ({ x: new Date(pt.date).getTime(), y: pt.value })
                        ),
                    },
                ];

                return (
                    <Stack direction="row" alignItems="center" sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', fontSize: 12 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', top: -6, mr: 1 }}>
                                <CustomLineChart
                                    series={sparkSeries}
                                    type="line"
                                    color="#7635dc"
                                    height={30}
                                    width={100}
                                    formatValue={(value: any) => fPercent(value * 100)}
                                />
                            </Box>
                            {fPercent(cur)}
                        </Box>

                        <Tooltip
                            title={
                                <TooltipInfoValue
                                    labelText="Blended CTR:"
                                    valueText={fPercent(prev)}
                                />
                            }
                            placement="top"
                            arrow
                            slotProps={{
                                popper: {
                                    sx: {
                                        [`& .MuiTooltip-tooltip`]: {
                                            backgroundColor: 'transparent',
                                        },
                                        [`& .MuiTooltip-arrow`]: {
                                            color: 'rgb(31, 38, 49)', // même couleur pour l'arrow !
                                        },
                                    }
                                }
                            }}
                            PopperProps={{ disablePortal: true }}
                        >
                            <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
                                <CompareChip value={delta_pp} />
                            </Box>
                        </Tooltip>
                    </Stack>
                );
            }
        },
        {
            field: "blended_cpc_total",
            headerName: "Blended CPC",
            type: "number",
            width: 265,
            renderCell: (params: GridRenderCellParams<any>) => {
                const cur = params.row.blended_cpc_total as number;
                const prev = params.row.blended_cpc_total_compare as number;
                const pct = prev ? ((cur - prev) / prev) * 100 : NaN;
                const sparkSeries = [
                    {
                        name: 'Blended CPC',
                        data: (params.row.sparkline_blended_cpc || []).map((pt: any) => ({
                            x: new Date(pt.date).getTime(),
                            y: pt.value,
                        })),
                    },
                ];
                return (
                    <Stack direction="row" alignItems="center" sx={{ flexDirection: 'row', justifyContent: 'space-between', overflow: 'visible' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', fontSize: 12 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    position: 'relative',
                                    top: -6,
                                    mr: 1,
                                    overflow: 'visible'
                                }}>
                                <CustomLineChart
                                    series={sparkSeries}
                                    type="line"
                                    color="#FF5630"
                                    height={30}
                                    width={100}
                                    formatValue={(value: any) => value?.toFixed(3)}
                                />
                            </Box>
                            {formatCurrency(cur, params.row.workspace_currency || workspace?.currency, lang, false, 2)}
                        </Box>

                        <Tooltip
                            title={
                                <TooltipInfoValue
                                    labelText="Blended CPC"
                                    valueText={formatCurrency(prev, params.row.workspace_currency || workspace?.currency, lang, false, 2)}
                                />
                            }
                            slotProps={{
                                popper: {
                                    sx: {
                                        [`& .MuiTooltip-tooltip`]: {
                                            backgroundColor: 'transparent',
                                        },
                                        [`& .MuiTooltip-arrow`]: {
                                            color: 'rgb(31, 38, 49)', // même couleur pour l'arrow !
                                        },
                                    }
                                }
                            }}
                            placement="top"
                            arrow
                            PopperProps={{ disablePortal: true }}
                        >
                            <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
                                <CompareChip invertedColor={true} value={pct} />
                            </Box>
                        </Tooltip>
                    </Stack>
                );
            }
        },
        {
            field: "brand_searches_total",
            type: "number",
            headerName: "Brand Searches",
            width: 265,
            renderCell: (params: GridRenderCellParams<any>) => {
                const cur = params.row.brand_searches_total as number;
                const prev = params.row.brand_searches_total_compare as number;
                const pct = prev ? ((cur - prev) / prev) * 100 : NaN;   // <-- Multiplie par 100 ici !
                const sparkSeries = [
                    {
                        name: 'Brand Searches',
                        data: (params.row.sparkline_brand_searches || []).map((pt: any) => ({
                            x: new Date(pt.date).getTime(),
                            y: pt.value,
                        })),
                    },
                ];
                return (
                    <Stack direction="row" alignItems="center" sx={{ flexDirection: 'row', justifyContent: 'space-between', overflow: 'visible' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', fontSize: 12 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    position: 'relative',
                                    top: -6,
                                    mr: 1,
                                    overflow: 'visible'
                                }}>
                                <CustomLineChart
                                    series={sparkSeries}
                                    type="line"
                                    color="#db8377"
                                    height={30}
                                    width={100}
                                    formatValue={(value: any) => formatNumber(value)}
                                />
                            </Box>
                            {formatNumber(cur)}
                        </Box>

                        <Tooltip
                            title={
                                <TooltipInfoValue
                                    labelText="Brand Searches"
                                    valueText={formatNumber(prev)}
                                />
                            }
                            slotProps={{
                                popper: {
                                    sx: {
                                        [`& .MuiTooltip-tooltip`]: {
                                            backgroundColor: 'transparent',
                                        },
                                        [`& .MuiTooltip-arrow`]: {
                                            color: 'rgb(31, 38, 49)', // même couleur pour l'arrow !
                                        },
                                    }
                                }
                            }}
                            placement="top"
                            arrow
                            PopperProps={{ disablePortal: true }}
                        >
                            <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
                                <CompareChip value={pct} />
                            </Box>
                        </Tooltip>
                    </Stack>
                );
            }
        },
        {
            field: "blended_conversions_total",
            type: "number",
            headerName: "Brand Conversions / Revenue",
            width: 300,
            renderCell: (params: GridRenderCellParams<any>) => {
                const cur = params.row.blended_conversions_total as number;
                const prev = params.row.blended_conversions_total_compare as number;
                const pct = prev ? ((cur - prev) / prev) * 100 : NaN;

                const sparkSeries = [
                    {
                        name: 'Brand Conversions',
                        data: (params.row.sparkline_brand_conversions || []).map((pt: any) => ({
                            x: new Date(pt.date).getTime(),
                            y: pt.value,
                        })),
                    },
                ];

                return (
                    <Stack
                        direction="row"
                        alignItems="center"
                        sx={{ flexDirection: 'row', justifyContent: 'space-between', overflow: 'visible' }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'row', fontSize: 12 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    position: 'relative',
                                    top: -6,
                                    mr: 1,
                                    overflow: 'visible',
                                }}
                            >
                                <CustomLineChart
                                    series={sparkSeries}
                                    type="line"
                                    color="#006c9c"
                                    height={30}
                                    width={100}
                                    formatValue={(value: any) =>
                                        params.row.workspace_template === 'E-commerce'
                                            ? formatCurrency(value, params.row.workspace_currency || workspace?.currency, lang, false, 0)
                                            : formatNumber(value)
                                    }
                                />
                            </Box>
                            {params.row.workspace_template === 'E-commerce'
                                ? formatCurrency(cur, params.row.workspace_currency || workspace?.currency, lang, false, 0)
                                : formatNumber(cur)}
                        </Box>

                        <Tooltip
                            title={
                                <TooltipInfoValue
                                    labelText="Brand Conversions"
                                    valueText={
                                        params.row.workspace_template === 'E-commerce'
                                            ? formatCurrency(prev, params.row.workspace_currency || workspace?.currency, lang, false, 0)
                                            : formatNumber(prev)
                                    } />
                            }
                            slotProps={{
                                popper: {
                                    sx: {
                                        [`& .MuiTooltip-tooltip`]: {
                                            backgroundColor: 'transparent',
                                        },
                                        [`& .MuiTooltip-arrow`]: {
                                            color: 'rgb(31, 38, 49)', // même couleur pour l'arrow !
                                        },
                                    }
                                }
                            }}
                            placement="top"
                            arrow
                            PopperProps={{ disablePortal: true }}
                        >
                            <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
                                {pct ? <CompareChip value={pct} /> : '-'}
                            </Box>
                        </Tooltip>
                    </Stack>
                );
            }
        },
        {
            field: "ad_cost_activated",
            type: "number",
            headerName: "Ad Cost Activated",
            width: 300,
            renderCell: (params: GridRenderCellParams<any>) => {
                const rawCur = params.row.ad_cost_activated as number;
                const cur = Number.isFinite(rawCur) ? rawCur : 0;
                const rawPrev = params.row.ad_cost_activated_compare as number;
                const prev = Number.isFinite(rawPrev) ? rawPrev : 0;
                const pct = prev > 0 ? ((cur - prev) / prev) * 100 : 0;

                const isPassive = (params.row.activations?.length || 0) === 0;
                if (isPassive) return "-";

                // Sparkline série
                const sparkSeries = [
                    {
                        name: 'Ad Cost Activated',
                        data: (params.row.sparkline_ad_cost_activated || []).map(
                            (pt: any) => ({ x: new Date(pt.date).getTime(), y: pt.value })
                        ),
                    },
                ];

                return (
                    <Stack direction="row" alignItems="center" sx={{ flexDirection: 'row', justifyContent: 'space-between', overflow: 'visible' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', fontSize: 12 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    position: 'relative',
                                    top: -6,
                                    mr: 1,
                                    overflow: 'visible'
                                }}>
                                <CustomLineChart
                                    series={sparkSeries}
                                    type="bar"
                                    color="#FFD066"
                                    height={30}
                                    width={100}
                                    formatValue={(value: any) =>
                                        formatCurrency(value, params.row.workspace_currency || workspace?.currency, lang, true, 2)
                                    }
                                />
                            </Box>
                            {formatCurrency(
                                cur,
                                params.row.workspace_currency || workspace?.currency,
                                lang,
                                true,
                                2
                            )}
                        </Box>

                        <Tooltip
                            title={
                                <TooltipInfoValue
                                    labelText="Ad Cost Activated:"
                                    valueText={formatCurrency(prev, params.row.workspace_currency || workspace?.currency, lang, true, 2)} z
                                />
                            }
                            placement="top"
                            slotProps={{
                                popper: {
                                    sx: {
                                        [`& .MuiTooltip-tooltip`]: {
                                            backgroundColor: 'transparent',
                                        },
                                        [`& .MuiTooltip-arrow`]: {
                                            color: 'rgb(31, 38, 49)', // même couleur pour l'arrow !
                                        },
                                    }
                                }
                            }}
                            arrow
                            PopperProps={{ disablePortal: true }}
                        >
                            <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
                                <CompareChip invertedColor={true} value={pct} />
                            </Box>
                        </Tooltip>

                    </Stack>
                );
            },
        },
        {
            field: "blended_ctr_activated",
            type: "number",
            headerName: "Blended CTR Activated",
            width: 300,
            renderCell: (params: GridRenderCellParams<any>) => {
                const cur = (params.row.blended_ctr_activated as number) * 100;
                const prev = (params.row.blended_ctr_activated_compare as number) * 100;
                const pct = prev ? (cur - prev) / prev : NaN;

                const isPassive = (params.row.activations?.length || 0) === 0;
                if (isPassive) return "-";

                const sparkSeries = [
                    {
                        name: 'Blended CTR Activated',
                        data: (params.row.sparkline_ctr_activated || []).map(
                            (pt: any) => ({ x: new Date(pt.date).getTime(), y: pt.value * 100 })
                        ),
                    },
                ];

                return (
                    <Stack direction="row" alignItems="center" sx={{ flexDirection: 'row', justifyContent: 'space-between', overflow: 'visible' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', fontSize: 12 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    position: 'relative',
                                    top: -6,
                                    mr: 1,
                                    overflow: 'visible'
                                }}>
                                <CustomLineChart
                                    series={sparkSeries}
                                    type="line"
                                    color="#7635dc"
                                    height={30}
                                    width={100}
                                    formatValue={(value: any) => fPercent(value)}
                                />
                            </Box>
                            {fPercent(cur)}
                        </Box>

                        <Tooltip
                            title={
                                <TooltipInfoValue
                                    labelText="Blended CTR Activated:"
                                    valueText={fPercent(prev)}
                                />
                            }
                            slotProps={{
                                popper: {
                                    sx: {
                                        [`& .MuiTooltip-tooltip`]: {
                                            backgroundColor: 'transparent',
                                        },
                                        [`& .MuiTooltip-arrow`]: {
                                            color: 'rgb(31, 38, 49)', // même couleur pour l'arrow !
                                        },
                                    }
                                }
                            }}
                            placement="top"
                            arrow
                            PopperProps={{ disablePortal: true }}
                        >
                            <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
                                <CompareChip value={pct} />
                            </Box>
                        </Tooltip>
                    </Stack>
                );
            },
        },
        {
            field: "blended_cpc_activated",
            type: "number",
            headerName: "Blended CPC Activated",
            width: 300,
            renderCell: (params: GridRenderCellParams<any>) => {
                const rawCur = params.row.blended_cpc_activated as number;
                const cur = Number.isFinite(rawCur) ? rawCur : 0;
                const rawPrev = params.row.blended_cpc_activated_compare as number;
                const prev = Number.isFinite(rawPrev) ? rawPrev : 0;
                const pct = prev > 0 ? ((cur - prev) / prev) * 100 : 0;

                const isPassive = (params.row.activations?.length || 0) === 0;
                if (isPassive) return "-";

                const sparkSeries = [
                    {
                        name: 'Blended CPC Activated',
                        data: (params.row.sparkline_cpc_activated || []).map(
                            (pt: any) => ({ x: new Date(pt.date).getTime(), y: pt.value })
                        ),
                    },
                ];

                return (
                    <Stack direction="row" alignItems="center" sx={{ flexDirection: 'row', justifyContent: 'space-between', overflow: 'visible' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', fontSize: 12 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    position: 'relative',
                                    top: -6,
                                    mr: 1,
                                    overflow: 'visible'
                                }}>
                                <CustomLineChart
                                    series={sparkSeries}
                                    type="line"
                                    color="#FF5630"
                                    height={30}
                                    width={100}
                                    formatValue={(value: any) => value?.toFixed(3)}
                                />
                            </Box>

                            {formatCurrency(
                                cur,
                                params.row.workspace_currency || workspace?.currency,
                                lang,
                                true,
                                2
                            )}

                        </Box>

                        <Tooltip
                            title={
                                <TooltipInfoValue
                                    labelText="Blended CPC Activated"
                                    valueText={formatCurrency(prev, params.row.workspace_currency || workspace?.currency, lang, false, 2)}
                                />
                            }
                            slotProps={{
                                popper: {
                                    sx: {
                                        [`& .MuiTooltip-tooltip`]: {
                                            backgroundColor: 'transparent',
                                        },
                                        [`& .MuiTooltip-arrow`]: {
                                            color: 'rgb(31, 38, 49)', // même couleur pour l'arrow !
                                        },
                                    }
                                }
                            }}
                            placement="top"
                            arrow
                            PopperProps={{ disablePortal: true }}
                        >
                            <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
                                <CompareChip invertedColor={true} value={pct} />
                            </Box>
                        </Tooltip>
                    </Stack>
                );
            },
        },
        {
            field: "activations",
            headerName: "Activations",
            width: 225,
            renderCell: (params: GridRenderCellParams<any>) => {
                const activations = params.row.activations || [];

                if (activations.length === 0) {
                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                flexWrap: 'wrap',
                                gap: 1,
                                height: '100%',
                            }}
                        >
                            <Box
                                sx={{
                                    px: 1,
                                    py: 0.5,
                                    border: `1px solid #c0c8cf`,
                                    color: '#c0c8cf',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 12,
                                    borderRadius: 1,
                                    height: 24,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Passive
                            </Box>
                        </Box>
                    );
                }

                const colorMap: Record<string, string> = {
                    'KO Bidding': '#d9bdfd',
                    'Active Mode': '#fdca61',
                };

                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            flexWrap: 'wrap',
                            gap: 1,
                            height: '100%',
                        }}
                    >
                        {activations.map((label: string, idx: number) => {
                            const color = colorMap[label] || '#ccc';

                            return (
                                <Box
                                    key={idx}
                                    sx={{
                                        px: 1,
                                        py: 0.5,
                                        border: `1px solid ${color}`,
                                        color: color,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 12,
                                        borderRadius: 1,
                                        height: 24,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {label}
                                </Box>
                            );
                        })}
                    </Box>
                );
            },
        },
        {
            field: 'Actions',
            headerName: '',
            width: 50,
            sortable: false,
            filterable: false,
            disableExport: true,
            renderCell: (params: GridRenderCellParams) =>
                params.row.isParent ? <ActionMenu params={params} /> : null,
        }

    ];

    const getTreeDataPath = React.useCallback(
        (row: any) => {
            const ws = (row.workspace_name || row.client || `unknown-${row.id}`)?.trim();
            const country = row.country?.trim();

            return row.parentId && country ? [ws, country] : [ws];
        },
        []
    );


    const CustomToolbar = () => (
        <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
        }}>
            <GridToolbarContainer sx={{ backgroundColor: 'transparent!important', borderBottom: '0px solid transparent' }}>
                <Box className="collumns-button-tooltip">
                    <GridToolbarColumnsButton />
                </Box>
                <GridToolbarFilterButton />
                <GridToolbarExport />
            </GridToolbarContainer>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderRadius: 1,
                    py: 2,
                    px: 2
                }}
            >
                <Stack direction="row" spacing={2}>
                    <Autocomplete
                        disablePortal
                        options={dateRangeOptions}
                        size="small"
                        disableClearable
                        value={selectedDateRange}
                        onChange={(event, newValue: any) => setSelectedDateRange(newValue)}
                        getOptionLabel={(option) => option.label}
                        sx={{ width: 300 }}
                        renderInput={(params) => <TextField {...params} label="Date" />}
                    />
                    <Autocomplete
                        disablePortal
                        options={comparisonPeriodOptions}
                        value={selectedComparisonPeriod}
                        disableClearable
                        size="small"
                        onChange={(event, newValue: any) => setSelectedComparisonPeriod(newValue)}
                        getOptionLabel={(option) => option.label}
                        sx={{ width: 300 }}
                        renderInput={(params) => <TextField {...params} label="Comparison" />}
                    />

                </Stack>
            </Box>
        </Box>
    );

    const getTabCount = (label: any) => {
        if (label === 'All') {
            return treeRows.filter(r => r.isParent).length;
        }

        let rule: ((row: any) => boolean) | null = null;

        if (label === 'Blended CTR dropped of 3pp') {
            rule = (row) =>
                typeof row.blended_ctr === 'number' &&
                typeof row.blended_ctr_compare === 'number' &&
                row.blended_ctr_compare &&
                (row.blended_ctr * 100 - row.blended_ctr_compare * 100) <= -3;
        }

        if (label === 'Blended CPC increased of 5%') {
            rule = (row) =>
                typeof row.blended_cpc_total === 'number' &&
                typeof row.blended_cpc_total_compare === 'number' &&
                row.blended_cpc_total_compare &&
                (row.blended_cpc_total - row.blended_cpc_total_compare) / row.blended_cpc_total_compare >= 0.05;
        }

        if (label === 'Brand Searches increased of 10%') {
            rule = (row) =>
                typeof row.brand_searches_total === 'number' &&
                typeof row.brand_searches_total_compare === 'number' &&
                row.brand_searches_total_compare &&
                (row.brand_searches_total - row.brand_searches_total_compare) / row.brand_searches_total_compare >= 0.10;
        }

        if (!rule) return 0;

        const parents = treeRows.filter(r => r.isParent);

        return parents.filter(parent => {
            const children = treeRows.filter(r => r.parentId === parent.id && !r.isParent);
            if (children.length === 0) {
                // Pas d'enfants : on teste la règle sur le parent lui-même
                return rule!(parent);
            }
            // Au moins un enfant valide la règle
            return children.some(rule!);
        }).length;
    };

    const CustomFooter = ({
        paginationModel,
        onPageSizeChange
    }: any) => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    px: 2,
                    py: 1,
                    borderTop: '1px solid #2c2f36',
                    gap: 2,
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>Rows per page:</Typography>
                    <Select
                        size="small"
                        value={paginationModel.pageSize}
                        onChange={(e) => onPageSizeChange(Number(e.target.value))}
                        sx={{ minWidth: 80 }}
                    >
                        {[5, 10, 20, 50].map((size) => (
                            <MenuItem key={size} value={size}>
                                {size}
                            </MenuItem>
                        ))}
                    </Select>

                    <GridPagination />
                </Stack>
            </Box>
        );
    };

    useEffect(() => {
        getCockpitData({
            current_range_date: selectedDateRange?.value,
            comparison_range_date: selectedComparisonPeriod?.value,
        });
    }, [selectedComparisonPeriod, selectedDateRange]);

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
        workspace_name: false,
        blended_cpc_activated: false,
        blended_ctr_activated: false,
        ad_cost_activated: false,
        blended_conversions_total: false,
    });

    const handleChangeColumnVisibilityModel = React.useCallback((newModel: GridColumnVisibilityModel) => {
        setColumnVisibilityModel(newModel);
    }, []);

    const filterAndKeepParents = (rows: any, matchFn: any) => {
        // Indexe les rows par id
        const byId = Object.fromEntries(rows.map((r: any) => [r.id, r]));
        // Trouve les rows qui matchent le filtre
        const matchedRows = rows.filter(matchFn);

        // Pour chaque row matchée, ajoute son parent si besoin
        const rowsToShow = new Map();

        function addWithParents(row: any) {
            if (!row) return;
            if (rowsToShow.has(row.id)) return;
            // Marque le row comme matché si c'est lui qui match, sinon non
            rowsToShow.set(row.id, { ...row, matched: matchFn(row) });
            if (row.parentId) {
                addWithParents(byId[row.parentId]);
            }
        }

        matchedRows.forEach((row: any) => addWithParents(row));
        return Array.from(rowsToShow.values());
    }


    const filteredRows = React.useMemo(() => {
        // Sélecteur de filtre selon l'onglet
        let matchFn: any = () => true; // par défaut (All)
        if (selectedTab === 'Blended CTR dropped of 3pp') {
            matchFn = (row: any) =>
                typeof row.blended_ctr === 'number' &&
                typeof row.blended_ctr_compare === 'number' &&
                row.blended_ctr_compare &&
                (row.blended_ctr * 100 - row.blended_ctr_compare * 100) <= -3;
        }
        if (selectedTab === 'Blended CPC increased of 5%') {
            matchFn = (row: any) =>
                typeof row.blended_cpc_total === 'number' &&
                typeof row.blended_cpc_total_compare === 'number' &&
                row.blended_cpc_total_compare &&
                (row.blended_cpc_total - row.blended_cpc_total_compare) / row.blended_cpc_total_compare >= 0.05;
        }
        if (selectedTab === 'Brand Searches increased of 10%') {
            matchFn = (row: any) =>
                typeof row.brand_searches_total === 'number' &&
                typeof row.brand_searches_total_compare === 'number' &&
                row.brand_searches_total_compare &&
                (row.brand_searches_total - row.brand_searches_total_compare) / row.brand_searches_total_compare >= 0.10;
        }
        // Filtres + parents, sinon tout
        return selectedTab === 'All' ? treeRows : filterAndKeepParents(treeRows, matchFn);
    }, [treeRows, selectedTab]);

    useEffect(() => {
        dispatch(setFilterDate({
            start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString()
        }))

        dispatch(setFilterCompare(false))

        dispatch(setFilterDateCompare({
            compareStart: subDays(new Date(), 60).toISOString(),
            compareEnd: subDays(new Date(), 31).toISOString(),
        }))
    }, [])

    return (
        <>
            <Box sx={{ pt: 2 }}>

                <Paper sx={{
                    backgroundColor: theme.palette.mode === "dark" ? '#212b36' : 'white',
                    height: 'auto',
                    borderRadius: 2,
                    pt: 1,
                }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 2,
                            pt: 1,
                            pb: 1,
                            gap: 2,
                        }}
                    >
                        {tabs.map((tab) => {
                            const isActive = selectedTab === tab.label;
                            return (
                                <Box
                                    key={tab.label}
                                    onClick={() => setSelectedTab(tab.label)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        fontWeight: isActive ? 600 : 500,
                                        px: .5,
                                        py: 1,
                                        borderRadius: 1,
                                        fontSize: 14,
                                        cursor: 'pointer',
                                        color: isActive
                                            ? theme.palette.mode === 'dark'
                                                ? '#fff'
                                                : 'black'
                                            : theme.palette.mode === 'dark'
                                                ? '#919eab'
                                                : '#374151',
                                        position: 'relative',
                                    }}
                                >
                                    {tab.label}
                                    <Box
                                        sx={{
                                            backgroundColor: isActive ? tab?.countBadgeActive : tab?.countBadge,
                                            color: isActive ? tab?.colorActive : tab?.color,
                                            fontWeight: 700,
                                            px: 1,
                                            py: .5,
                                            fontSize: 12,
                                            borderRadius: 1,
                                            minWidth: 20,
                                            textAlign: 'center',
                                        }}
                                    >
                                        {getTabCount(tab.label)}
                                    </Box>
                                    {isActive && (
                                        <Box
                                            sx={{
                                                content: '""',
                                                position: 'absolute',
                                                bottom: -8,
                                                left: 0,
                                                right: 0,
                                                height: 3,
                                                backgroundColor:
                                                    theme.palette.mode === 'dark' ? '#fff' : '#000',
                                                borderRadius: 2,
                                            }}
                                        />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    {isLoading ? (
                        <Skeleton width={'100%'} height={550} variant="rectangular" animation="wave" />
                    ) : (
                        <DataGridPro
                            sx={{
                                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
                                    outline: 'none !important',
                                },
                                '& .MuiDataGrid-cell--pinnedLeft': {
                                    backgroundColor: theme.palette.mode === "dark" ? '#212b36 !important' : 'white!important',
                                },
                                '& .MuiDataGrid-cell--pinnedRight': {
                                    backgroundColor: theme.palette.mode === "dark" ? '#212b36 !important' : 'white!important',
                                },
                                '& .MuiDataGrid-cellContent': {
                                    overflow: 'visible !important',
                                },
                                '& .highlighted-row': {
                                    opacity: 0.3,
                                },
                                overflow: 'visible !important',
                                '& .MuiDataGrid-cell': {
                                    overflow: 'visible !important',
                                },
                                '& .MuiTooltip-popper': {
                                    zIndex: 1500,
                                },
                            }}
                            loading={isLoading}
                            columnVisibilityModel={columnVisibilityModel}
                            onColumnVisibilityModelChange={handleChangeColumnVisibilityModel}
                            rows={filteredRows}
                            pinnedColumns={{ left: ['__tree_data_group__', 'Actions'] }}
                            columns={columns}
                            density="standard"
                            disableRowSelectionOnClick
                            treeData
                            getTreeDataPath={getTreeDataPath}
                            groupingColDef={{
                                headerName: 'Workspace',
                                width: 250,
                                sortable: true,
                                valueGetter: (params: any) => params?.row?.workspace_name || params?.row?.client,
                            }}
                            paginationModel={paginationModel}
                            pagination={true}
                            onPaginationModelChange={setPaginationModel}
                            getRowClassName={(params) => params.row.matched ? '' : selectedTab !== 'All' ? 'highlighted-row' : ''}
                            hideFooterSelectedRowCount
                            slots={{
                                toolbar: CustomToolbar,
                                footer: () => (
                                    <CustomFooter
                                        paginationModel={paginationModel}
                                        onPageSizeChange={(newSize: number) =>
                                            setPaginationModel((prev) => ({ ...prev, pageSize: newSize, page: 0 }))
                                        }
                                        rowCount={treeRows.length}
                                    />
                                )
                            }}
                        />
                    )}
                </Paper>
            </Box>
        </>
    );
}